import React from 'react';

import { createImages } from '@vlabs/api/luna-api/client';
import type { LunaClient } from '@vlabs/api-bindings/src';
import { apiContainer } from '@vlabs/api-bindings/src';
import { routes } from '@vlabs/shared/navigation/routes';
import { useModal } from '@vlabs/uikit';
import qs from 'qs';
import { generatePath, useHistory } from 'react-router-dom';

import { SERVER_IMAGE_MIME_TYPES } from './constants';
import { ImageCheckForm } from './ImageCheckForm';
import type { TImageCheckFormValues } from './types';

interface UseImageCheckFormProps extends TImageCheckFormValues {
  fileList?: FileList;
  liveness: boolean;
  deepfake: boolean;
  iso: boolean;
  icao: boolean;
  mindigital: boolean;
}

interface TClickableControl {
  onClick: VoidFunction;
}
interface UseImageCheckFormReturn {
  wrap: (Component: React.JSXElementConstructor<TClickableControl>) => React.JSX.Element;
}

function useImageCheckForm(formProps?: UseImageCheckFormProps): UseImageCheckFormReturn {
  const modal = useModal();
  const history = useHistory();

  const lunaClient = (apiContainer.lunaClient as LunaClient);
  const onSubmit = async ({ fileList, ...checks }: TImageCheckFormValues) => {
    const file = fileList?.[0];
    if (!file) return;

    const resp = await lunaClient.objects.create(file, {
      headers: {
        'Content-type': file.type,
      },
    });
    const objectId = resp.data.object_id;
    let imageId: string | undefined;
    if (SERVER_IMAGE_MIME_TYPES.includes(file.type)) {
      const image = await createImages(file, undefined, {
        headers: { 'Content-Type': file.type },
      });
      imageId = image.image_id;
    }
    const route = generatePath(routes.checks.imageChecks, { objectId });
    const queryParams = qs.stringify({ ...checks, filename: file.name, imageId });
    history.push(`${route}?${queryParams}`);
  };

  return {
    wrap: (Component: React.JSXElementConstructor<TClickableControl>): React.JSX.Element => {
      return (
        <React.Fragment>
          {modal.wrap(<ImageCheckForm
            {...formProps}
            onCancel={modal.close}
            onSubmit={onSubmit}
          />)}
          <Component onClick={modal.open} />
        </React.Fragment>
      );
    },
  };
}

export { useImageCheckForm };
